/**
 * Leitura do frontmatter do `SKILL.md`.
 *
 * E um subconjunto de YAML deliberado, nao um parser: escalares, listas em
 * bloco (`- item`), listas em linha (`[a, b]`) e mapas aninhados. Cobre o que
 * o padrao Agent Skills define — `name`, `description`, `title`, `license`,
 * `allowed-tools`, `metadata` — e recusa o que nao entende, em vez de adivinhar.
 *
 * === Por que o aninhamento passou de um nivel para varios (AB#85634) ===
 *
 * A spec so precisa de um: `metadata` e um mapa de texto para texto. Mas o que
 * uma skill declara ALEM da spec — os parametros que a instalacao coleta, as
 * integracoes que ela exige — nao cabe em texto plano sem virar uma mini-DSL
 * dentro de uma string. Foi o que `param-` era, e ela ja batia no teto: nao
 * separa rotulo de descricao, nao tem ordem, nao tem ajuda multilinha, nao
 * expressa faixa nem campo condicional. O mercado escreve isso em YAML
 * aninhado com vocabulario de JSON Schema — `inputs` do GitHub Actions,
 * `values.yaml` do Helm — e e para esse formato que este leitor abre espaco.
 *
 * O teto continua existindo, em MAX_DEPTH niveis. Nao e um limite tecnico: e a
 * recusa de virar um parser de YAML completo pela porta dos fundos.
 *
 * A alternativa seria trazer um parser de YAML como dependencia de runtime. Um
 * pacote que so declara regras nao deveria arrastar uma; e um parser completo
 * aceitaria ancoras, tags e merge keys, que ampliam a superficie de um formato
 * que o ecossistema ja trata como hostil (ver as premissas de seguranca do
 * epico AB#85629).
 */
const DELIMITER = '---';
/**
 * Quantos niveis de mapa o formato aceita.
 *
 * Quatro cobre `parameters` > uma chave > um campo, com uma folga. Mais fundo
 * do que isso, numa declaracao de skill, e sinal de que a coisa pertence a
 * outro arquivo do pacote.
 */
const MAX_DEPTH = 4;
export function parseFrontmatter(source) {
    // BOM e CRLF chegam de editor de Windows e de upload do navegador.
    const text = source.replace(/^﻿/, '').replace(/\r\n/g, '\n');
    const lines = text.split('\n');
    if (lines[0]?.trim() !== DELIMITER) {
        return { ok: false, reason: 'missing', detail: 'file does not open with a frontmatter block' };
    }
    const closing = lines.findIndex((line, index) => index > 0 && line.trim() === DELIMITER);
    if (closing === -1) {
        return { ok: false, reason: 'missing', detail: 'frontmatter block is not closed' };
    }
    const data = {};
    // Os blocos abertos, de fora para dentro. Pilha vazia, escreve-se na raiz.
    const stack = [];
    const aberto = () => (stack.length > 0 ? stack[stack.length - 1] : null);
    /**
     * Fecha os blocos que esta linha ja deixou para tras.
     *
     * Um bloco continua aberto enquanto a linha vem na coluna dos filhos dele ou
     * mais fundo; voltar para a esquerda fecha. Bloco que fecha sem nenhum filho
     * fica com a lista vazia que ja tinha — e o que o arquivo de fato diz.
     */
    const fechaAte = (indent) => {
        while (stack.length > 0) {
            const topo = stack[stack.length - 1];
            const dentro = topo.childIndent === null ? indent > topo.parentIndent : indent >= topo.childIndent;
            if (dentro)
                break;
            stack.pop();
        }
    };
    for (let index = 1; index < closing; index += 1) {
        const line = lines[index].trimEnd();
        if (line.trim() === '' || line.trimStart().startsWith('#'))
            continue;
        const indent = line.length - line.trimStart().length;
        const content = line.trimStart();
        fechaAte(indent);
        const bloco = aberto();
        // Item de lista. Em YAML ele pode vir alinhado com a propria chave, entao a
        // indentacao nao serve para decidir: o que decide e haver bloco aberto.
        const listItem = /^-\s+(.*)$/.exec(content);
        if (listItem) {
            if (!bloco) {
                return { ok: false, reason: 'invalid', detail: `list item without a key at line ${index + 1}` };
            }
            if (bloco.kind === 'map') {
                return { ok: false, reason: 'invalid', detail: `line ${index + 1} mixes a list item into a map` };
            }
            if (bloco.kind === 'unknown') {
                bloco.kind = 'list';
                bloco.container[bloco.key] = bloco.list;
            }
            bloco.list.push(parseScalar(listItem[1]));
            continue;
        }
        const pair = /^([A-Za-z0-9_-]+)\s*:\s*(.*)$/.exec(content);
        if (!pair) {
            return { ok: false, reason: 'invalid', detail: `line ${index + 1} is not a key/value pair` };
        }
        const [, key, rawValue] = pair;
        const value = rawValue.trim();
        if (!bloco && indent > 0) {
            return { ok: false, reason: 'invalid', detail: `line ${index + 1} is indented under no key` };
        }
        // Onde este par vai parar: dentro do bloco aberto, ou na raiz.
        let destino = data;
        if (bloco) {
            if (bloco.kind === 'list') {
                return { ok: false, reason: 'invalid', detail: `line ${index + 1} mixes a map entry into a list` };
            }
            if (bloco.kind === 'unknown') {
                bloco.kind = 'map';
                bloco.container[bloco.key] = {};
            }
            // O primeiro filho fixa a coluna e os seguintes tem de casar com ela. Um
            // desalinho no meio do bloco e erro de quem escreveu; adivinhar a que
            // bloco a linha pertence mudaria o sentido do arquivo em silencio.
            if (bloco.childIndent === null)
                bloco.childIndent = indent;
            else if (indent !== bloco.childIndent) {
                return { ok: false, reason: 'invalid', detail: `line ${index + 1} is misaligned` };
            }
            destino = bloco.container[bloco.key];
        }
        if (value === '') {
            // Chave sem valor abre um bloco. Fica como lista vazia ate o primeiro
            // filho dizer o que ela e — e se nao vier nenhum, lista vazia e o que o
            // arquivo diz, e nao vale inventar `null` no lugar.
            if (stack.length >= MAX_DEPTH) {
                return {
                    ok: false,
                    reason: 'invalid',
                    detail: `line ${index + 1} nests deeper than this format accepts`,
                };
            }
            destino[key] = [];
            stack.push({
                key,
                kind: 'unknown',
                parentIndent: indent,
                childIndent: null,
                container: destino,
                list: [],
            });
            continue;
        }
        destino[key] = parseScalar(value);
    }
    return { ok: true, data, body: lines.slice(closing + 1).join('\n').replace(/^\n+/, '') };
}
function parseScalar(value) {
    const text = value.trim();
    if (text.startsWith('[') && text.endsWith(']')) {
        const inner = text.slice(1, -1).trim();
        if (inner === '')
            return [];
        return inner.split(',').map((part) => parseScalar(part));
    }
    if ((text.startsWith('"') && text.endsWith('"') && text.length >= 2) ||
        (text.startsWith("'") && text.endsWith("'") && text.length >= 2)) {
        return text.slice(1, -1);
    }
    if (text === 'true')
        return true;
    if (text === 'false')
        return false;
    if (/^-?\d+(\.\d+)?$/.test(text))
        return Number(text);
    return text;
}
//# sourceMappingURL=frontmatter.js.map